import type { IKType, IKTypeField, IWFParameter } from "@kuika/kuika-cl-model";
import { foldParamNameForMatch } from "./workflow-form-metadata-overrides";

/**
 * KOA-18136 — scoping a TASK FEED to one process, and choosing which of that
 * process's fields ride back with every task row.
 *
 * ## What the human panel actually writes
 *
 * The "Select from single process" switch on a task-feed action (`GetUserTasks`,
 * and the deprecated `GetMyTasksFromProcess`) is not one setting — it writes
 * THREE records, and all three are load-bearing:
 *
 *   1. the action's `ProcessDefinitionId` parameter ← the workflow's **id**;
 *   2. its `ProcessParameters` parameter ← the selected parameters' **ids**,
 *      comma-joined (codegen resolves ids → names against the LATEST workflow
 *      version, so renaming a parameter never breaks the binding);
 *   3. the action instance's `returnTypeDynamicInterface` ← one element per
 *      selected parameter, `{ name, prefix: "data", type }`.
 *
 * The third is the one nothing else can substitute for. A table's column picker
 * offers `static return fields + this action instance's dynamic elements`, so
 * without (3) the parameter is neither offered nor bindable, the cell-binding
 * enrichment cannot resolve `data.<Param>` (no `fieldToDisplay`, no `fieldType`),
 * and React codegen never appends the `.Value` reader — it prints the bag entry
 * object instead of the value. Writing (1)+(2) alone therefore produces a screen
 * that LOOKS configured and shows nothing.
 *
 * Backend validation is deliberately lenient about `data.<Param>` bindings (it
 * cannot tell "parameter removed" from "dynamic interface not introspectable",
 * so it never blocks generation on a guess). A half-written scope is thus
 * INVISIBLE until someone opens the running app. That is why this module refuses
 * rather than partially applies: the three records are computed together, or
 * nothing is written.
 *
 * ## Which parameters may become row fields
 *
 * A process declares parameters in two families, and the distinction is authored,
 * not guessed: BUSINESS-OBJECT parameters hold what the requester submitted, and
 * INTERNAL parameters are the engine's bookkeeping (decision flags, multi-instance
 * counters, carrier ids). Only the business family may reach an approver's inbox —
 * an internal parameter on screen is a leak of machinery, and it is what made the
 * platform's admin instance read unusable for requester-facing screens (KOA-17362
 * §25 refused `GetProcessData` partly for this reason).
 *
 * Within the business family the CALLER chooses. This module does not select for
 * it: a task inbox showing every field of a 20-parameter process is as wrong as
 * one showing none.
 *
 * Pure — no services, no designer state. The caller supplies the workflow.
 */

/** The one parameter name that carries the workflow's id on a task-feed action. */
export const TASK_FEED_PROCESS_PARAM: string = "ProcessDefinitionId";

/** The one parameter name that carries the selected parameter ids. */
export const TASK_FEED_FIELDS_PARAM: string = "ProcessParameters";

/**
 * The prefix every process-parameter row field carries.
 *
 * Pinned to the panel's own value. It is what makes the element a TOP-LEVEL field
 * with a path rather than a walk into the static `Data` element (which is an
 * unrelated `{Key,Value,Type}` shape), and codegen keys the value reader on it.
 */
export const TASK_FEED_FIELD_PREFIX: string = "data";

/** Action names whose panel honours this configuration. */
const TASK_FEED_ACTION_NAMES: ReadonlySet<string> = new Set<string>(["getusertasks", "getmytasksfromprocess"]);

/** True when `actionName` is a task feed that can be scoped to a single process. */
export function isTaskFeedAction(actionName: string | undefined): boolean {
  if (actionName === undefined) return false;
  return TASK_FEED_ACTION_NAMES.has(actionName.trim().toLowerCase());
}

/** Structural subset of a workflow this module reads. */
export interface ITaskFeedWorkflowLike {
  id?: string;
  parameters?: IWFParameter[];
}

/** One parameter offered as a task-row field. */
export interface ITaskFeedFieldOption {
  /** The workflow parameter's id — what gets stored. */
  readonly id: string;
  /** The parameter's name — what the row field is called (`data.<name>`). */
  readonly name: string;
  /** Backend data type, carried onto the dynamic element. */
  readonly type: string;
  /** The bindable path a table cell uses. */
  readonly fieldPath: string;
}

/** Everything the writer needs, once the request has been checked. */
export interface ITaskFeedScopePlan {
  readonly processId: string;
  /** Comma-joined parameter ids — the stored form. */
  readonly parameterIdList: string;
  /** The dynamic result contract: one element per selected parameter. */
  readonly dynamicInterface: IKType;
  /** The fields a caller may now bind, in the requested order. */
  readonly fields: readonly ITaskFeedFieldOption[];
}

export type TaskFeedScopeRefusalCode =
  | "not-a-task-feed"
  | "process-not-found"
  | "no-business-parameters"
  | "unknown-parameter"
  | "internal-parameter";

export interface ITaskFeedScopeRefusal {
  readonly refused: TaskFeedScopeRefusalCode;
  readonly message: string;
  /** The business parameters that WERE available, so the caller can retry once. */
  readonly availableFields: readonly string[];
}

export type TaskFeedScopeOutcome = ITaskFeedScopePlan | ITaskFeedScopeRefusal;

export function isTaskFeedScopeRefusal(outcome: TaskFeedScopeOutcome): outcome is ITaskFeedScopeRefusal {
  return (outcome as ITaskFeedScopeRefusal).refused !== undefined;
}

/**
 * The process's BUSINESS-OBJECT parameters, in declaration order.
 *
 * `isBusinessObject` is the authored flag (`PARAM BO` in the process script) and
 * is the ONLY signal used: a name-shape heuristic would be locale-dependent and
 * would misfile a Turkish-named bookkeeping flag as business data.
 */
export function listTaskFeedFieldOptions(workflow: ITaskFeedWorkflowLike | undefined): ITaskFeedFieldOption[] {
  const parameters: readonly IWFParameter[] = workflow?.parameters ?? [];
  const out: ITaskFeedFieldOption[] = [];
  const seen: Set<string> = new Set<string>();
  for (const parameter of parameters) {
    if (parameter.isBusinessObject !== true) continue;
    const id: string = String(parameter.id ?? "").trim();
    const name: string = String(parameter.name ?? "").trim();
    // Both halves are load-bearing: the id is what gets stored, the name is what
    // the row field is called. A parameter missing either cannot produce a field.
    if (id.length === 0 || name.length === 0 || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name,
      type: String(parameter.dataType ?? "String"),
      fieldPath: `${TASK_FEED_FIELD_PREFIX}.${name}`
    });
  }
  return out;
}

/** The internal (bookkeeping) parameter names — named only to refuse them by name. */
function internalParameterNames(workflow: ITaskFeedWorkflowLike | undefined): Set<string> {
  const out: Set<string> = new Set<string>();
  for (const parameter of workflow?.parameters ?? []) {
    if (parameter.isBusinessObject === true) continue;
    const name: string = String(parameter.name ?? "").trim();
    if (name.length > 0) out.add(foldParamNameForMatch(name));
  }
  return out;
}

function refuse(
  code: TaskFeedScopeRefusalCode,
  message: string,
  available: readonly ITaskFeedFieldOption[]
): ITaskFeedScopeRefusal {
  return {
    refused: code,
    message,
    availableFields: available.map((f: ITaskFeedFieldOption): string => f.name)
  };
}

export interface IResolveTaskFeedScopeInput {
  readonly actionName: string | undefined;
  readonly workflow: ITaskFeedWorkflowLike | undefined;
  readonly processId: string;
  /**
   * The parameters to bring back, by NAME or by id. Order is preserved so the
   * caller's column order survives.
   */
  readonly requestedFields: readonly string[];
}

/**
 * Compute the three records, or refuse with the reason and the real options.
 *
 * Never partially succeeds: an unresolvable field aborts the whole plan, because
 * the alternative — writing the resolvable subset — silently drops a column the
 * caller believes it configured, and nothing downstream reports the loss.
 */
export function resolveTaskFeedScope(input: IResolveTaskFeedScopeInput): TaskFeedScopeOutcome {
  const available: ITaskFeedFieldOption[] = listTaskFeedFieldOptions(input.workflow);

  if (!isTaskFeedAction(input.actionName)) {
    return refuse(
      "not-a-task-feed",
      `"${input.actionName ?? "(unknown)"}" is not a task feed. Only GetUserTasks carries a single-process scope ` +
        "with process fields on its rows.",
      available
    );
  }

  const processId: string = input.processId.trim();
  if (processId.length === 0 || input.workflow === undefined) {
    return refuse(
      "process-not-found",
      `Process "${input.processId}" could not be read, so its parameters cannot be resolved. ` +
        "Pass a workflow id from ui_project_extended.list_workflows.",
      available
    );
  }

  if (available.length === 0) {
    return refuse(
      "no-business-parameters",
      "This process declares no business-object parameters, so there is nothing a task row could carry. " +
        "Scope the feed only when the approver must see fields the requester submitted.",
      available
    );
  }

  const internal: Set<string> = internalParameterNames(input.workflow);
  const byFolded: Map<string, ITaskFeedFieldOption> = new Map<string, ITaskFeedFieldOption>();
  for (const option of available) {
    byFolded.set(foldParamNameForMatch(option.name), option);
    byFolded.set(foldParamNameForMatch(option.id), option);
  }

  const fields: ITaskFeedFieldOption[] = [];
  const chosen: Set<string> = new Set<string>();
  for (const requested of input.requestedFields) {
    const raw: string = String(requested ?? "").trim();
    if (raw.length === 0) continue;
    // A caller that already knows the bindable path may pass it verbatim.
    const bare: string = raw.toLowerCase().startsWith(`${TASK_FEED_FIELD_PREFIX}.`)
      ? raw.slice(TASK_FEED_FIELD_PREFIX.length + 1)
      : raw;
    const folded: string = foldParamNameForMatch(bare);
    const option: ITaskFeedFieldOption | undefined = byFolded.get(folded);
    if (option === undefined) {
      if (internal.has(folded)) {
        return refuse(
          "internal-parameter",
          `"${bare}" is an INTERNAL process parameter — engine bookkeeping (decision flags, counters, carrier ` +
            "ids), not something the requester submitted. Internal parameters never become task-row fields.",
          available
        );
      }
      return refuse("unknown-parameter", `"${bare}" is not a business-object parameter of this process.`, available);
    }
    if (chosen.has(option.id)) continue;
    chosen.add(option.id);
    fields.push(option);
  }

  return {
    processId,
    parameterIdList: fields.map((f: ITaskFeedFieldOption): string => f.id).join(","),
    dynamicInterface: buildTaskFeedDynamicInterface(fields),
    fields
  };
}

/**
 * The dynamic result contract for a set of fields.
 *
 * Shape pinned to the panel's writer: `elementTypeModel` absent (a leaf, so the
 * column picker's non-nested filter keeps it), `prefix` = "data", and `type`
 * carrying the parameter's declared data type.
 */
export function buildTaskFeedDynamicInterface(fields: readonly ITaskFeedFieldOption[]): IKType {
  const elements: IKTypeField[] = fields.map(
    (field: ITaskFeedFieldOption): IKTypeField =>
      ({
        elementTypeModel: undefined,
        name: field.name,
        prefix: TASK_FEED_FIELD_PREFIX,
        type: field.type as unknown as IKType
      }) as IKTypeField
  );
  return { elements } as unknown as IKType;
}

/**
 * The three records that turn the scope OFF again.
 *
 * Mirrors the panel's switch: both parameters cleared and the dynamic contract
 * emptied. Emptied rather than deleted — the writer only replaces what it is
 * given, and an absent contract would leave stale fields offered.
 */
export function clearedTaskFeedScope(): ITaskFeedScopePlan {
  return {
    processId: "",
    parameterIdList: "",
    dynamicInterface: { elements: [] } as unknown as IKType,
    fields: []
  };
}
